/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2026 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/**
 * @file Utility functions for experiment configuration management
 */

import { getEnvVar } from '../core/EnvUtil';
import { ExperimentsEnabledConfig, ExperimentConfig } from '../types/Experiments.d';

const IMS_CLIENT_ID_KEY = 'ims_client_id';

/**
 * Check if experiments are enabled for the app via launch options
 */
export const isExperimentsEnabled = (): boolean => {
  try {
    return window.adobe_dc_sdk?.launchOptions?.enableExperiments === true;
  } catch (e) {
    return false;
  }
};

/**
 * Get the IMS client ID from environment variables
 */
export const getClientId = (): string => getEnvVar(IMS_CLIENT_ID_KEY);

/**
 * Get the app identifier from launch options
 */
export const getAppId = (): string => {
  try {
    return window.adobe_dc_sdk?.launchOptions?.app_identifier || '';
  } catch (e) {
    return '';
  }
};

/**
 * Helper function to get value from localStorage
 */
export const getFromLocalStorage = (key: string): string => {
  try {
    if (window.localStorage) {
      return window.localStorage.getItem(key) || '';
    }
  } catch (e) {
    return '';
  }
  return '';
};

/**
 * Helper function to get experiments-enabled config from localStorage
 */
export const getExperimentsConfig = (): ExperimentsEnabledConfig | null => {
  try {
    const enabledExperiments = getFromLocalStorage('experiments-enabled');
    if (enabledExperiments) {
      return JSON.parse(enabledExperiments) as ExperimentsEnabledConfig;
    }
  } catch (e) {
    // Ignore parsing errors
  }
  return null;
};

/**
 * Helper function to get experiments for the current app and IMS client ID
 * Uses app ID from environment variable and client ID to find experiments in the nested config structure.
 * Both clientId and appId are required.
 * @param overrideClientId - Optional client ID to use instead of environment variable
 */
export const getExperimentsForCurrentClient = (overrideClientId?: string): ExperimentConfig[] => {
  // Check if experiments are enabled for the app
  if (!isExperimentsEnabled()) return [];

  const config = getExperimentsConfig();
  if (!config) return [];

  const clientId = overrideClientId || getClientId();
  const appId = getAppId();

  // Both clientId and appId are required
  if (!clientId || !appId) return [];

  // Look up experiments using app ID and client ID
  if (!config[appId]) return [];
  const experiments = config[appId][clientId] || [];

  // Filter out experiments that have passed their expiry
  return experiments.filter(exp => exp.expiry && Date.now() < exp.expiry);
};

/**
 * Helper function to get dropinVersions from enabled experiments for current client.
 * Only one experiment with dropinVersions can be enabled at a time, so we return
 * the dropinVersions from the first experiment that has it.
 */
export const getDropinVersions = (): string => {
  const experiments = getExperimentsForCurrentClient();
  if (experiments.length === 0) return '';

  // Find the first experiment with dropinVersions (only one should have it)
  const expWithDropins = experiments.find(exp => exp.dropinVersions);
  return expWithDropins?.dropinVersions || '';
};

/**
 * Helper function to get floodgate flags from enabled experiments for current client
 * @param overrideClientId - Optional client ID to use instead of environment variable
 */
export const getFloodgateFlagsFromExperiments = (overrideClientId?: string): string[] => {
  const experiments = getExperimentsForCurrentClient(overrideClientId);
  if (experiments.length === 0) return [];

  // Get floodgate flags from experiments for current IMS client ID
  const allFlags = new Set<string>();
  experiments.forEach(exp => {
    if (exp.floodgateFlags) {
      exp.floodgateFlags.forEach(flag => allFlags.add(flag));
    }
  });
  return Array.from(allFlags);
};
